import { useEffect, useMemo, useState } from "react";
import { Activity, BookOpenCheck, Boxes, Network, ShieldCheck, UserCog } from "lucide-react";
import { buildImpactReport } from "../shared/domain/index.js";
import type { AdminWorkspaceSnapshot } from "../shared/domain/index.js";
import type { TicketTemplate, TicketVersion } from "../shared/domain/index.js";
import type { TargetDefinition } from "../shared/domain/targetVisibility.js";

type TemplateSnapshotResponse = {
  template: TicketTemplate;
  versions?: TicketVersion[];
  latestVersion?: TicketVersion;
  editableTargets: TargetDefinition[];
  activePublications?: unknown[];
  publicationCount?: number;
};

type AdminSimulatorCatalogItem = {
  appId: string;
  name: string;
  family: string;
  manifestActive: boolean;
  targetDefinitionCount: number;
  commandCountLast30d: number;
  lastCommandAt: string | null;
  lastEvaluatorPassAt: string | null;
  status: "VOLLEDIG" | "BIJNA VOLLEDIG" | "SEMI" | "COMPAT-ONLY" | "PLACEHOLDER";
  latestCommands: Array<{ commandType: string; createdAt: string }>;
};

type AdminUserListItem = {
  id: string;
  email: string;
  displayName: string;
  role: string;
  roleLabel: string;
  active: boolean;
  institutionId: string | null;
  institutionName: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
};

type AdminInstitutionOption = {
  id: string;
  name: string;
};

type AdminUserListResponse = {
  items: AdminUserListItem[];
  institutions: AdminInstitutionOption[];
  summary: {
    total: number;
    active: number;
    inactive: number;
    byRole: Record<string, number>;
  };
};

type AdminInstitutionSummary = AdminInstitutionOption & {
  code: string | null;
  userCount: number;
  teacherCount: number;
  studentCount: number;
  publicationCount: number;
};

type AdminAuditEvent = {
  id: string;
  eventType: string;
  actorUserId: string | null;
  resourceKind: string;
  resourceId: string | null;
  createdAt: string;
};

function normalizeTemplateSnapshot(snapshot: TemplateSnapshotResponse): TemplateSnapshotResponse {
  const versions = snapshot.versions ?? (snapshot.latestVersion ? [snapshot.latestVersion] : []);
  return {
    ...snapshot,
    versions,
    latestVersion: snapshot.latestVersion ?? versions[versions.length - 1],
    editableTargets: snapshot.editableTargets ?? [],
    publicationCount: snapshot.publicationCount ?? snapshot.activePublications?.length ?? 0
  };
}

function formatExpected(value: unknown): string {
  if (value === undefined || value === null) return "{}";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function AdminWorkspace({ snapshot }: { snapshot: AdminWorkspaceSnapshot }) {
  const { activeManifestCount, manifestCount, migrationSteps, recentTemplates, simulatorApps, template } = snapshot;
  const impactReports = simulatorApps.map((app) => buildImpactReport(app, app.appId === "dns-manager" ? "functional" : "visual"));
  const counts = migrationSteps.map((step) => Number(step.match(/\d+/)?.[0] ?? 0));
  const [templates, setTemplates] = useState<TicketTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [selectedSnapshot, setSelectedSnapshot] = useState<TemplateSnapshotResponse | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isPublishingVersion, setIsPublishingVersion] = useState(false);
  const [templateMessage, setTemplateMessage] = useState<string>("");
  const [catalog, setCatalog] = useState<AdminSimulatorCatalogItem[]>([]);
  const [catalogFamilyFilter, setCatalogFamilyFilter] = useState<string>("all");
  const [catalogStatusFilter, setCatalogStatusFilter] = useState<string>("all");
  const [catalogSearch, setCatalogSearch] = useState<string>("");
  const [catalogSort, setCatalogSort] = useState<"status" | "activity">("status");
  const [selectedCatalogApp, setSelectedCatalogApp] = useState<string>("");
  const [catalogMessage, setCatalogMessage] = useState<string>("");
  const [adminUsers, setAdminUsers] = useState<AdminUserListItem[]>([]);
  const [adminInstitutions, setAdminInstitutions] = useState<AdminInstitutionOption[]>([]);
  const [adminUserSummary, setAdminUserSummary] = useState<AdminUserListResponse["summary"] | null>(null);
  const [adminUserSearch, setAdminUserSearch] = useState("");
  const [adminUserRoleFilter, setAdminUserRoleFilter] = useState("all");
  const [adminUserStatusFilter, setAdminUserStatusFilter] = useState("all");
  const [adminUserInstitutionFilter, setAdminUserInstitutionFilter] = useState("all");
  const [adminUserSortBy, setAdminUserSortBy] = useState("createdAt");
  const [adminUserRoleDrafts, setAdminUserRoleDrafts] = useState<Record<string, string>>({});
  const [adminUserMessage, setAdminUserMessage] = useState("");
  const [savingUserRoleId, setSavingUserRoleId] = useState("");
  const [institutionSummaries, setInstitutionSummaries] = useState<AdminInstitutionSummary[]>([]);
  const [auditEvents, setAuditEvents] = useState<AdminAuditEvent[]>([]);
  const [adminExtensionMessage, setAdminExtensionMessage] = useState("");
  const [form, setForm] = useState({
    title: "",
    description: "",
    topic: "",
    complexity: "",
    category: "",
    environment: "",
    status: "draft"
  });
  const topicOptions = ["identity", "networking", "filesystem", "security", "server", "m365_entra", "troubleshooting", "operations"] as const;
  const complexityOptions = ["intro", "basic", "intermediate", "advanced", "exam"] as const;
  const categoryOptions = ["game", "practice", "assessment"] as const;
  const environmentOptions = ["hybrid", "onprem", "cloud"] as const;
  const roleOptions = ["student", "teacher", "institution_admin", "platform_admin", "content_admin"];

  useEffect(() => {
    let canceled = false;
    async function loadAdminUsers() {
      const params = new URLSearchParams();
      if (adminUserSearch.trim()) params.set("q", adminUserSearch.trim());
      if (adminUserRoleFilter !== "all") params.set("role", adminUserRoleFilter);
      if (adminUserStatusFilter !== "all") params.set("status", adminUserStatusFilter);
      if (adminUserInstitutionFilter !== "all") params.set("institutionId", adminUserInstitutionFilter);
      params.set("sortBy", adminUserSortBy);
      params.set("sortDir", "desc");
      try {
        const response = await fetch(`/api/v2/admin/users?${params.toString()}`, { credentials: "include" });
        if (!response.ok) throw new Error(`admin_users_failed_${response.status}`);
        const data = await response.json() as AdminUserListResponse;
        if (canceled) return;
        setAdminUsers(data.items ?? []);
        setAdminInstitutions(data.institutions ?? []);
        setAdminUserSummary(data.summary ?? null);
        setAdminUserRoleDrafts((current) => {
          const next = { ...current };
          for (const user of data.items ?? []) {
            if (!next[user.id]) next[user.id] = user.role;
          }
          return next;
        });
      } catch {
        if (!canceled) setAdminUserMessage("Gebruikersbeheer laden is mislukt.");
      }
    }
    void loadAdminUsers();
    return () => {
      canceled = true;
    };
  }, [adminUserInstitutionFilter, adminUserRoleFilter, adminUserSearch, adminUserSortBy, adminUserStatusFilter]);

  useEffect(() => {
    let canceled = false;
    async function loadTemplates() {
      setLoadingTemplates(true);
      try {
        const response = await fetch("/api/v2/content/ticket-templates", { credentials: "include" });
        if (!response.ok) throw new Error(`template_list_failed_${response.status}`);
        const data = await response.json() as { templates?: TicketTemplate[] };
        if (canceled) return;
        const nextTemplates = data.templates ?? [];
        setTemplates(nextTemplates);
        if (!selectedTemplateId && nextTemplates.length > 0) {
          setSelectedTemplateId(nextTemplates[0].id);
        }
      } catch {
        if (!canceled) setTemplateMessage("Templatecatalogus laden is mislukt.");
      } finally {
        if (!canceled) setLoadingTemplates(false);
      }
    }
    void loadTemplates();
    return () => { canceled = true; };
  }, [selectedTemplateId]);

  useEffect(() => {
    let canceled = false;
    async function loadCatalog() {
      try {
        const response = await fetch("/api/v2/admin/simulator-catalog", { credentials: "include" });
        if (!response.ok) throw new Error(`catalog_failed_${response.status}`);
        const data = await response.json() as { items?: AdminSimulatorCatalogItem[] };
        if (canceled) return;
        const nextItems = data.items ?? [];
        setCatalog(nextItems);
        if (!selectedCatalogApp && nextItems.length > 0) {
          setSelectedCatalogApp(nextItems[0].appId);
        }
      } catch {
        if (!canceled) setCatalogMessage("Simulatorcatalogus laden is mislukt.");
      }
    }
    void loadCatalog();
    return () => {
      canceled = true;
    };
  }, [selectedCatalogApp]);

  useEffect(() => {
    let canceled = false;
    async function loadAdminExtensions() {
      try {
        const [institutionsResponse, auditResponse] = await Promise.all([
          fetch("/api/v2/admin/institutions", { credentials: "include" }),
          fetch("/api/v2/admin/audit-events", { credentials: "include" })
        ]);
        if (institutionsResponse.ok) {
          const payload = await institutionsResponse.json() as { institutions?: AdminInstitutionSummary[] };
          if (!canceled) setInstitutionSummaries(payload.institutions ?? []);
        }
        if (auditResponse.ok) {
          const payload = await auditResponse.json() as { events?: AdminAuditEvent[] };
          if (!canceled) setAuditEvents(payload.events ?? []);
        }
      } catch {
        if (!canceled) setAdminExtensionMessage("Admin-overzicht laden is mislukt.");
      }
    }
    void loadAdminExtensions();
    return () => { canceled = true; };
  }, []);

  async function runAdminRollout() {
    setAdminExtensionMessage("");
    try {
      const response = await fetch("/api/v2/admin/ticket-generator/rollout/run", {
        method: "POST",
        credentials: "include"
      });
      if (!response.ok) throw new Error(`admin_rollout_failed_${response.status}`);
      const payload = await response.json() as { batchesActivated?: number; publicationsActivated?: number };
      setAdminExtensionMessage(`Rollout uitgevoerd: ${payload.batchesActivated ?? 0} batches, ${payload.publicationsActivated ?? 0} publicaties.`);
    } catch {
      setAdminExtensionMessage("Rollout uitvoeren is mislukt.");
    }
  }

  useEffect(() => {
    if (!selectedTemplateId) return;
    let canceled = false;
    async function loadSnapshot() {
      try {
        const response = await fetch(`/api/v2/content/ticket-templates/${encodeURIComponent(selectedTemplateId)}`, {
          credentials: "include"
        });
        if (!response.ok) throw new Error(`template_snapshot_failed_${response.status}`);
        const snapshotResponse = normalizeTemplateSnapshot(await response.json() as TemplateSnapshotResponse);
        if (canceled) return;
        setSelectedSnapshot(snapshotResponse);
        setForm({
          title: snapshotResponse.template.title,
          description: snapshotResponse.template.description,
          topic: snapshotResponse.template.topic,
          complexity: snapshotResponse.template.complexity,
          category: snapshotResponse.template.category,
          environment: snapshotResponse.template.environment,
          status: snapshotResponse.template.status
        });
      } catch {
        if (!canceled) setTemplateMessage("Template-detail laden is mislukt.");
      }
    }
    void loadSnapshot();
    return () => { canceled = true; };
  }, [selectedTemplateId]);

  const recentVersionLabel = useMemo(() => {
    const versions = selectedSnapshot?.versions ?? [];
    const latestVersion = versions[versions.length - 1] ?? selectedSnapshot?.latestVersion;
    return latestVersion ? `v${latestVersion.versionNumber}` : "geen versie";
  }, [selectedSnapshot]);

  const familyOptions = useMemo(() => {
    return ["all", ...Array.from(new Set(catalog.map((item) => item.family))).sort((left, right) => left.localeCompare(right, "nl-NL"))];
  }, [catalog]);

  const statusOptions = useMemo(() => {
    return ["all", ...Array.from(new Set(catalog.map((item) => item.status)))];
  }, [catalog]);

  const filteredCatalog = useMemo(() => {
    const statusOrder = ["VOLLEDIG", "BIJNA VOLLEDIG", "SEMI", "COMPAT-ONLY", "PLACEHOLDER"];
    const normalizedSearch = catalogSearch.trim().toLowerCase();
    return catalog
      .filter((item) => catalogFamilyFilter === "all" || item.family === catalogFamilyFilter)
      .filter((item) => catalogStatusFilter === "all" || item.status === catalogStatusFilter)
      .filter((item) => normalizedSearch.length === 0
        || item.name.toLowerCase().includes(normalizedSearch)
        || item.appId.toLowerCase().includes(normalizedSearch))
      .sort((left, right) => {
        if (catalogSort === "status") {
          const statusDiff = statusOrder.indexOf(left.status) - statusOrder.indexOf(right.status);
          if (statusDiff !== 0) return statusDiff;
          return left.name.localeCompare(right.name, "nl-NL");
        }
        const leftDate = left.lastCommandAt ? Date.parse(left.lastCommandAt) : 0;
        const rightDate = right.lastCommandAt ? Date.parse(right.lastCommandAt) : 0;
        return rightDate - leftDate;
      });
  }, [catalog, catalogFamilyFilter, catalogSearch, catalogSort, catalogStatusFilter]);

  const selectedCatalogItem = useMemo(() => {
    return filteredCatalog.find((item) => item.appId === selectedCatalogApp) ?? filteredCatalog[0] ?? null;
  }, [filteredCatalog, selectedCatalogApp]);
  const isTemplateDirty = useMemo(() => {
    if (!selectedSnapshot) return false;
    const source = selectedSnapshot.template;
    return form.title !== source.title
      || form.description !== source.description
      || form.topic !== source.topic
      || form.complexity !== source.complexity
      || form.category !== source.category
      || form.environment !== source.environment
      || form.status !== source.status;
  }, [form, selectedSnapshot]);

  async function saveAdminUserRole(user: AdminUserListItem) {
    const nextRole = adminUserRoleDrafts[user.id] ?? user.role;
    if (nextRole === user.role || savingUserRoleId) return;
    setSavingUserRoleId(user.id);
    setAdminUserMessage("");
    try {
      const response = await fetch(`/api/v2/admin/users/${encodeURIComponent(user.id)}/role`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ role: nextRole })
      });
      if (!response.ok) {
        if (response.status === 409) {
          setAdminUserMessage("Laatste platform-admin mag niet worden gedegradeerd.");
          return;
        }
        throw new Error(`admin_user_role_failed_${response.status}`);
      }
      const updated = await response.json() as AdminUserListItem;
      setAdminUsers((current) => current.map((item) => item.id === updated.id ? updated : item));
      setAdminUserRoleDrafts((current) => ({ ...current, [updated.id]: updated.role }));
      setAdminUserMessage(`Rol opgeslagen voor ${updated.displayName}.`);
    } catch {
      setAdminUserMessage("Rol wijzigen is mislukt.");
    } finally {
      setSavingUserRoleId("");
    }
  }

  async function saveTemplate() {
    if (!selectedTemplateId) return;
    setIsSavingTemplate(true);
    setTemplateMessage("");
    try {
      const response = await fetch(`/api/v2/content/ticket-templates/${encodeURIComponent(selectedTemplateId)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      if (!response.ok) throw new Error(`template_patch_failed_${response.status}`);
      const updated = await response.json() as TicketTemplate;
      setSelectedSnapshot((prev) => prev ? { ...prev, template: updated } : prev);
      setTemplates((prev) => prev.map((templateItem) => templateItem.id === updated.id ? updated : templateItem));
      setTemplateMessage("Template opgeslagen.");
    } catch {
      setTemplateMessage("Opslaan van template is mislukt.");
    } finally {
      setIsSavingTemplate(false);
    }
  }

  async function publishTemplateVersion() {
    if (!selectedTemplateId || !selectedSnapshot) return;
    setIsPublishingVersion(true);
    setTemplateMessage("");
    try {
      const response = await fetch(`/api/v2/content/ticket-templates/${encodeURIComponent(selectedTemplateId)}/publish`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          persist: true,
          targets: selectedSnapshot.editableTargets
        })
      });
      if (!response.ok) throw new Error(`template_publish_failed_${response.status}`);
      const version = await response.json() as TicketVersion;
      setSelectedSnapshot((prev) => prev
        ? {
            ...prev,
            latestVersion: version,
            versions: [...(prev.versions ?? []), version]
          }
        : prev
      );
      setTemplateMessage(`Nieuwe versie gepubliceerd: v${version.versionNumber}`);
    } catch {
      setTemplateMessage("Publiceren van nieuwe versie is mislukt.");
    } finally {
      setIsPublishingVersion(false);
    }
  }

  function scrollToAdminSection(sectionId: string) {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function openTemplateManagement(templateId?: string) {
    if (templateId) {
      setSelectedTemplateId(templateId);
    }
    scrollToAdminSection("admin-content");
  }

  return (
    <>
      <header className="fd-app-page-header" id="admin-overview">
        <div className="fd-app-page-header-left">
          <span className="fd-app-kicker">Instellingsbeheer</span>
          <h1>Beheerdashboard</h1>
          <p className="fd-app-page-desc">Gebruikers, content, simulator-apps en migratiecontrole voor FirstDesk v2.</p>
        </div>
      </header>

      <section className="fd-kpi-grid fd-v2-admin-kpis">
        {[
          ["Users", counts[0] ?? 0],
          ["Tickets", counts[1] ?? 0],
          ["Templates", counts[2] ?? 0],
          ["Scenario's", counts[3] ?? 0]
        ].map(([label, value]) => (
          <div className="fd-kpi-card" key={String(label)}>
            <p className="fd-kpi-label">{label}</p>
            <h3 className="fd-kpi-value">{value}</h3>
          </div>
        ))}
      </section>

      <section className="fd-v2-admin-extension-grid" id="admin-extensions">
        <div className="fd-bento-stat fd-bento-stat--white">
          <div className="fd-section-head">
            <h2 className="fd-section-title">Instellingen</h2>
            <span className="fd-status-badge">{institutionSummaries.length} tenants</span>
          </div>
          <div className="fd-v2-calendar-list">
            {institutionSummaries.slice(0, 6).map((institution) => (
              <div className="fd-v2-calendar-row" key={institution.id}>
                <span>{institution.name}</span>
                <strong>{institution.studentCount} studenten</strong>
              </div>
            ))}
          </div>
        </div>
        <div className="fd-bento-stat fd-bento-stat--white">
          <div className="fd-section-head">
            <h2 className="fd-section-title">Audit-log</h2>
            <button className="fd-table-action" type="button" onClick={() => void runAdminRollout()}>Rollout draaien</button>
          </div>
          <div className="fd-v2-calendar-list">
            {auditEvents.slice(0, 6).map((event) => (
              <div className="fd-v2-calendar-row" key={event.id}>
                <span>{event.eventType}</span>
                <strong>{new Date(event.createdAt).toLocaleDateString("nl-NL")}</strong>
              </div>
            ))}
          </div>
          {adminExtensionMessage ? <p className="fd-table-sub">{adminExtensionMessage}</p> : null}
        </div>
      </section>

      <section className="fd-bento-stat fd-bento-stat--white fd-v2-content-editor" id="admin-users">
        <div className="fd-section-head">
          <h2 className="fd-section-title">
            <UserCog aria-hidden="true" size={19} />
            Gebruikersbeheer
          </h2>
          <span className="fd-status-badge fd-status-badge--active">{adminUserSummary?.total ?? adminUsers.length} gebruikers</span>
        </div>
        <div className="fd-v2-admin-summary-row">
          <span>Actief <strong>{adminUserSummary?.active ?? 0}</strong></span>
          <span>Inactief <strong>{adminUserSummary?.inactive ?? 0}</strong></span>
          {Object.entries(adminUserSummary?.byRole ?? {}).slice(0, 5).map(([role, count]) => (
            <span key={role}>{role} <strong>{count}</strong></span>
          ))}
        </div>
        <div className="fd-v2-admin-user-toolbar">
          <label className="fd-v2-grow">
            Zoeken
            <input
              value={adminUserSearch}
              onChange={(event) => setAdminUserSearch(event.target.value)}
              placeholder="Naam of e-mail"
            />
          </label>
          <label>
            Rol
            <select value={adminUserRoleFilter} onChange={(event) => setAdminUserRoleFilter(event.target.value)}>
              <option value="all">Alle rollen</option>
              {roleOptions.map((role) => <option key={role} value={role}>{role}</option>)}
            </select>
          </label>
          <label>
            Status
            <select value={adminUserStatusFilter} onChange={(event) => setAdminUserStatusFilter(event.target.value)}>
              <option value="all">Alle statussen</option>
              <option value="active">Actief</option>
              <option value="inactive">Inactief</option>
            </select>
          </label>
          <label>
            Instituut
            <select value={adminUserInstitutionFilter} onChange={(event) => setAdminUserInstitutionFilter(event.target.value)}>
              <option value="all">Alle instituten</option>
              {adminInstitutions.map((institution) => (
                <option key={institution.id} value={institution.id}>{institution.name}</option>
              ))}
            </select>
          </label>
          <label>
            Sortering
            <select value={adminUserSortBy} onChange={(event) => setAdminUserSortBy(event.target.value)}>
              <option value="createdAt">Aangemaakt</option>
              <option value="lastLoginAt">Laatste login</option>
              <option value="displayName">Naam</option>
              <option value="role">Rol</option>
            </select>
          </label>
        </div>
        {adminUserMessage ? <p className="fd-v2-inline-message">{adminUserMessage}</p> : null}
        <div className="fd-app-table-wrap">
          <table className="fd-app-table">
            <thead>
              <tr>
                <th>Gebruiker</th>
                <th>Instituut</th>
                <th>Rol</th>
                <th>Status</th>
                <th>Laatste login</th>
                <th>Aangemaakt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {adminUsers.map((user) => {
                const roleDraft = adminUserRoleDrafts[user.id] ?? user.role;
                return (
                  <tr key={user.id}>
                    <td>
                      <p className="fd-table-name">{user.displayName}</p>
                      <p className="fd-table-sub">{user.email}</p>
                    </td>
                    <td>{user.institutionName}</td>
                    <td>
                      <select
                        value={roleDraft}
                        onChange={(event) => setAdminUserRoleDrafts((current) => ({ ...current, [user.id]: event.target.value }))}
                      >
                        {roleOptions.map((role) => <option key={role} value={role}>{role}</option>)}
                      </select>
                    </td>
                    <td><span className={`fd-status-badge ${user.active ? "fd-status-badge--active" : ""}`}>{user.active ? "Actief" : "Inactief"}</span></td>
                    <td>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString("nl-NL") : "Nooit"}</td>
                    <td>{new Date(user.createdAt).toLocaleDateString("nl-NL")}</td>
                    <td className="fd-col-right">
                      <button
                        className="fd-table-action"
                        type="button"
                        disabled={roleDraft === user.role || savingUserRoleId === user.id}
                        onClick={() => void saveAdminUserRole(user)}
                      >
                        {savingUserRoleId === user.id ? "Opslaan..." : "Opslaan"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {adminUsers.length === 0 ? <p className="fd-table-sub">Geen gebruikers gevonden voor deze filters.</p> : null}
        </div>
      </section>

      <div className="fd-app-grid-2">
        <div className="fd-v2-admin-stack">
          <div className="fd-bento-stat fd-bento-stat--white fd-v2-side-card" id="admin-settings">
            <h2 className="fd-section-title">
              <span className="material-symbols-outlined">bolt</span>
              Snelle acties
            </h2>
            <div className="admin-actions">
              <button className="fd-app-btn fd-app-btn--gradient" type="button" onClick={() => scrollToAdminSection("admin-users")}>
                <span className="material-symbols-outlined">manage_accounts</span>
                Gebruikers beheren
              </button>
              <button className="fd-app-btn" type="button" onClick={() => openTemplateManagement()}>
                <span className="material-symbols-outlined">analytics</span>
                Tickets aanmaken
              </button>
              <button className="fd-app-btn" type="button" onClick={() => scrollToAdminSection("admin-settings")}>
                <span className="material-symbols-outlined">settings</span>
                Instellingen
              </button>
            </div>
          </div>

          <div className="fd-bento-stat fd-bento-stat--white fd-v2-side-card">
            <div className="fd-section-head">
              <h2 className="fd-section-title">
                <ShieldCheck aria-hidden="true" size={19} />
                Seed promotie
              </h2>
              <span className="fd-status-badge fd-status-badge--active">Review</span>
            </div>
            <div className="admin-list">
              <article>
                <strong>{template.title}</strong>
                <small>Docenttemplate - {template.topic}/{template.complexity}</small>
                <div className="admin-actions">
                  <button className="fd-app-btn fd-app-btn--primary" type="button" onClick={() => openTemplateManagement(template.id)}>Promoveren</button>
                  <button className="fd-app-btn fd-app-btn--outline" type="button" onClick={() => openTemplateManagement(template.id)}>Corrigeren</button>
                </div>
              </article>
            </div>
          </div>

          <div className="fd-bento-stat fd-bento-stat--white fd-v2-side-card">
            <div className="fd-section-head">
              <h2 className="fd-section-title">
                <Boxes aria-hidden="true" size={19} />
                Database manifesten
              </h2>
              <span className="fd-status-badge fd-status-badge--active">{activeManifestCount} actief</span>
            </div>
            <div className="migration-steps fd-v2-migration-list">
              <article>
                <span>1</span>
                <strong>{manifestCount} manifests gepersist</strong>
                <small>Upsert vanuit de v2 app-registry naar `simulator_app_manifests`.</small>
              </article>
              <article>
                <span>2</span>
                <strong>{activeManifestCount} actieve manifests</strong>
                <small>DB-backed inventaris voor admin-impact en latere contentgovernance.</small>
              </article>
            </div>
          </div>

          <div className="fd-bento-stat fd-bento-stat--white fd-v2-side-card">
            <h2 className="fd-section-title">
              <Activity aria-hidden="true" size={19} />
              Migratie readiness
            </h2>
            <div className="migration-steps fd-v2-migration-list">
              {migrationSteps.map((item, index) => (
                <article key={item}>
                  <span>{index + 1}</span>
                  <strong>{item}</strong>
                  <small>Gecontroleerde v1 naar v2 mapping</small>
                </article>
              ))}
            </div>
          </div>
        </div>

        <div className="fd-bento-stat fd-bento-stat--white fd-v2-side-card fd-v2-impact-card" id="admin-content">
          <div className="fd-section-head">
            <h2 className="fd-section-title">
              <Network aria-hidden="true" size={19} />
              Simulator-app impact
            </h2>
            <span className="fd-status-badge fd-status-badge--active">{simulatorApps.length} manifests</span>
          </div>
          <div className="fd-app-table-wrap fd-v2-impact-table">
            <table className="fd-app-table">
              <thead>
                <tr>
                  <th>App</th>
                  <th>Capabilities</th>
                  <th>Targets</th>
                  <th>Impact</th>
                </tr>
              </thead>
              <tbody>
                {simulatorApps.map((app) => {
                  const report = impactReports.find((item) => item.appId === app.appId);
                  return (
                    <tr key={app.appId}>
                      <td>
                        <div className="fd-table-user">
                          <div className="fd-table-avatar">{app.name.slice(0, 2).toUpperCase()}</div>
                          <div>
                            <p className="fd-table-name">{app.name}</p>
                            <p className="fd-table-sub">{app.category} - {app.commands.length} command</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="chip-row">
                          {app.capabilities.slice(0, 2).map((capability) => (
                            <span className="chip" key={capability}>{capability}</span>
                          ))}
                        </div>
                      </td>
                      <td><span className="fd-table-score">{app.targetBindings.length}</span></td>
                      <td>
                        <span className={report?.requiresEvaluatorReview ? "risk-high" : "risk-low"}>
                          {report?.requiresEvaluatorReview ? "Evaluator review" : "UI-safe"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="fd-announce-card fd-v2-admin-note">
            <h4 className="fd-announce-title">Content governance</h4>
            <p className="fd-announce-text">
              Admin-edits moeten altijd via templateversies en immutable attempts lopen, zodat historische studentdata niet stuk gaat.
            </p>
            <button className="fd-announce-link" type="button" onClick={() => openTemplateManagement()}>
              Review wachtrij
              <BookOpenCheck aria-hidden="true" size={16} />
            </button>
          </div>

          <div className="fd-announce-card fd-v2-admin-note">
            <div className="fd-section-head">
              <h4 className="fd-announce-title">Simulator app health</h4>
              <span className="fd-status-badge">{filteredCatalog.length} apps</span>
            </div>
            <div className="admin-actions" style={{ marginBottom: "0.75rem", gap: "0.5rem", flexWrap: "wrap" }}>
              <input
                value={catalogSearch}
                onChange={(event) => setCatalogSearch(event.target.value)}
                placeholder="Zoek app of appId"
                aria-label="Zoek simulator app"
                style={{ minWidth: "180px" }}
              />
              <select value={catalogFamilyFilter} onChange={(event) => setCatalogFamilyFilter(event.target.value)}>
                {familyOptions.map((family) => (
                  <option key={family} value={family}>
                    {family === "all" ? "Alle families" : family}
                  </option>
                ))}
              </select>
              <select value={catalogStatusFilter} onChange={(event) => setCatalogStatusFilter(event.target.value)}>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status === "all" ? "Alle statussen" : status}
                  </option>
                ))}
              </select>
              <select value={catalogSort} onChange={(event) => setCatalogSort(event.target.value as "status" | "activity")}>
                <option value="status">Sorteer op status</option>
                <option value="activity">Sorteer op activiteit</option>
              </select>
            </div>
            <div className="fd-app-table-wrap fd-v2-impact-table" data-admin-simulator-catalog="true">
              <table className="fd-app-table">
                <thead>
                  <tr>
                    <th>App</th>
                    <th>Status</th>
                    <th>Cmd 30d</th>
                    <th>Targets</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCatalog.map((item) => (
                    <tr
                      key={item.appId}
                      onClick={() => setSelectedCatalogApp(item.appId)}
                      style={{ cursor: "pointer", background: selectedCatalogItem?.appId === item.appId ? "rgba(94, 114, 228, 0.08)" : undefined }}
                    >
                      <td>
                        <p className="fd-table-name">{item.name}</p>
                        <p className="fd-table-sub">{item.appId}</p>
                      </td>
                      <td><span className="fd-status-badge">{item.status}</span></td>
                      <td>{item.commandCountLast30d}</td>
                      <td>{item.targetDefinitionCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredCatalog.length === 0 ? <p className="fd-table-sub">Geen apps gevonden voor deze filter.</p> : null}
            {catalogMessage ? <p className="fd-table-sub">{catalogMessage}</p> : null}
            {selectedCatalogItem ? (
              <div className="fd-v2-template-editor" data-admin-simulator-catalog-details="true">
                <p className="fd-table-name">{selectedCatalogItem.name}</p>
                <p className="fd-table-sub">
                  Laatste command: {selectedCatalogItem.lastCommandAt ? new Date(selectedCatalogItem.lastCommandAt).toLocaleString("nl-NL") : "geen"}
                </p>
                <p className="fd-table-sub">
                  Laatste evaluator pass: {selectedCatalogItem.lastEvaluatorPassAt ? new Date(selectedCatalogItem.lastEvaluatorPassAt).toLocaleString("nl-NL") : "geen"}
                </p>
                <div className="chip-row">
                  {selectedCatalogItem.latestCommands.slice(0, 10).map((command) => (
                    <span key={`${command.commandType}-${command.createdAt}`} className="chip">
                      {command.commandType}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="fd-announce-card fd-v2-admin-note">
            <div className="fd-section-head">
              <h4 className="fd-announce-title">Templatecatalogus</h4>
              <span className="fd-status-badge">{templates.length || recentTemplates.length} recent</span>
            </div>
            <div className="fd-app-table-wrap fd-v2-impact-table">
              <table className="fd-app-table">
                <thead>
                  <tr>
                    <th>Titel</th>
                    <th>Topic</th>
                    <th>Status</th>
                    <th>Laatst bijgewerkt</th>
                  </tr>
                </thead>
                <tbody>
                  {(templates.length > 0 ? templates : recentTemplates).map((item) => {
                    const updatedLabel = "updatedLabel" in item
                      ? item.updatedLabel
                      : "-";
                    return (
                    <tr key={item.id}>
                      <td>
                        <p className="fd-table-name">{item.title}</p>
                        <p className="fd-table-sub">{item.complexity ?? "-"}</p>
                      </td>
                      <td>{item.topic}</td>
                      <td><span className="fd-status-badge">{item.status}</span></td>
                      <td>{updatedLabel}</td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>

            <div className="fd-announce-card fd-v2-admin-note">
              <div className="fd-section-head">
                <h4 className="fd-announce-title">Templatebeheer</h4>
                <span className="fd-status-badge">{recentVersionLabel}</span>
              </div>
              <div className="admin-actions" style={{ marginBottom: "0.75rem" }}>
                <select
                  value={selectedTemplateId}
                  onChange={(event) => setSelectedTemplateId(event.target.value)}
                  disabled={loadingTemplates || templates.length === 0}
                >
                  {(templates.length > 0 ? templates : recentTemplates).map((templateOption) => (
                    <option key={templateOption.id} value={templateOption.id}>
                      {templateOption.title}
                    </option>
                  ))}
                </select>
              </div>
              {selectedSnapshot ? (
                <div className="fd-v2-template-editor" data-template-editor="true">
                  <label>
                    Titel
                    <input
                      value={form.title}
                      onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                    />
                  </label>
                  <label>
                    Beschrijving
                    <textarea
                      rows={3}
                      value={form.description}
                      onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                    />
                  </label>
                  <div className="fd-v2-template-editor-grid">
                    <label>
                      Topic
                      <select value={form.topic} onChange={(event) => setForm((prev) => ({ ...prev, topic: event.target.value }))}>
                        {topicOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </label>
                    <label>
                      Complexiteit
                      <select value={form.complexity} onChange={(event) => setForm((prev) => ({ ...prev, complexity: event.target.value }))}>
                        {complexityOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </label>
                    <label>
                      Categorie
                      <select value={form.category} onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}>
                        {categoryOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </label>
                    <label>
                      Omgeving
                      <select value={form.environment} onChange={(event) => setForm((prev) => ({ ...prev, environment: event.target.value }))}>
                        {environmentOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </label>
                  </div>
                  <p className="fd-table-sub">{isTemplateDirty ? "Niet-opgeslagen wijzigingen" : "Geen open wijzigingen"}</p>
                  <div className="fd-v2-template-targets" aria-label="Template targets">
                    <div className="fd-v2-row-between">
                      <div>
                        <strong>Targets in laatste versie</strong>
                        <p>{selectedSnapshot.editableTargets.length} target(s), {selectedSnapshot.publicationCount ?? 0} publicatie(s)</p>
                      </div>
                      <span className="fd-status-badge">Admin-only expected zichtbaar</span>
                    </div>
                    {selectedSnapshot.editableTargets.map((target, index) => (
                      <article className="fd-v2-template-target-card" key={target.id || `${target.title}-${index}`}>
                        <div>
                          <span className="fd-v2-create-eyebrow">Target {index + 1}</span>
                          <h5>{target.title}</h5>
                          <p>{target.description || "Geen beschrijving"}</p>
                        </div>
                        <dl>
                          <div><dt>Evaluator</dt><dd>{target.evaluatorKey}</dd></div>
                          <div><dt>Visibility</dt><dd>{target.visibility}</dd></div>
                          <div><dt>Scoring</dt><dd>{target.scoring}</dd></div>
                          <div><dt>Weight</dt><dd>{target.weight}</dd></div>
                        </dl>
                        <details>
                          <summary>Expected JSON en docentnotities</summary>
                          <pre>{formatExpected(target.expected)}</pre>
                          <p>{target.teacherNotes || "Geen docentnotities."}</p>
                        </details>
                      </article>
                    ))}
                    {selectedSnapshot.editableTargets.length === 0 ? <p className="fd-table-sub">Geen targets gevonden voor deze versie.</p> : null}
                  </div>
                  <div className="admin-actions">
                    <button className="fd-app-btn fd-app-btn--primary" type="button" onClick={() => void saveTemplate()} disabled={isSavingTemplate || !isTemplateDirty || !form.title.trim() || !form.description.trim()}>
                      {isSavingTemplate ? "Opslaan..." : "Opslaan"}
                    </button>
                    <button className="fd-app-btn fd-app-btn--outline" type="button" onClick={() => void publishTemplateVersion()} disabled={isPublishingVersion || isSavingTemplate}>
                      {isPublishingVersion ? "Publiceren..." : "Publiceer nieuwe versie"}
                    </button>
                  </div>
                  {templateMessage ? <p className="fd-table-sub">{templateMessage}</p> : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
